import { optimizedCompletion } from '@/lib/openai/speedOptimizer';
import { extractContent } from '@/lib/openai/modelRouter';
import { ApiError } from '@/lib/errors';

interface JobFormData {
  jobTitle: string;
  company: string;
  location?: string;
  industry?: string;
  experience?: string;
  skills: string;
  responsibilities: string;
  qualifications?: string;
  benefits?: string;
  companyMission?: string;
  // Advanced options
  tone?: 'professional' | 'conversational' | 'enthusiastic' | 'formal';
  includeEqualOpportunity?: boolean;
  formatWithMarkdown?: boolean;
  [key: string]: string | boolean | undefined;
}

export async function generateJobDescription(formData: Record<string, string | boolean>): Promise<string> {
  try {
    const jobData = formData as unknown as JobFormData;
    
    // Create a structured prompt for the AI
    const prompt = createJobDescriptionPrompt(jobData);
    
    const response = await optimizedCompletion('job-description', [
      {
        role: 'system',
        content: `You are an expert HR professional and technical recruiter specialized in creating compelling, professional job descriptions. 
        Create a well-structured, comprehensive job description based on the information provided. 
        Use ${jobData.tone || 'professional'} tone, clear formatting with sections, and compelling language that attracts qualified candidates.
        The job description should include: job title, company overview, location, key responsibilities, required skills, qualifications, experience requirements, and benefits.
        Format the output ${jobData.formatWithMarkdown ? 'using Markdown with headers (##), bullet points, and emphasis where appropriate' : 'using proper spacing, bullet points where appropriate, and section headers'}.
        Make it ready to be used in professional job postings.
        ${jobData.includeEqualOpportunity ? 'Include an Equal Employment Opportunity statement at the end of the job description.' : ''}
        Ensure the job description is engaging, accurate, and presents the company in the best possible light.`
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      temperature: 0.6, // Reduced for faster response
      max_tokens: 1200 // Reduced for faster response
    });

    return extractContent(response);
  } catch (error) {
    if (error instanceof Error) {
      throw new ApiError(error.message, 500);
    }
    throw new ApiError('Failed to generate job description', 500);
  }
}

function createJobDescriptionPrompt(jobData: JobFormData): string {
  // Build a detailed prompt from form data
  const tone = jobData.tone || 'professional';
  
  return `Please create a ${tone} job description for a ${jobData.jobTitle} position at ${jobData.company} with the following details:

Company: ${jobData.company}
${jobData.companyMission ? `Company Mission/Values: ${jobData.companyMission}` : ''}
${jobData.industry ? `Industry: ${jobData.industry}` : ''}
${jobData.location ? `Location: ${jobData.location}` : ''}
${jobData.experience ? `Years of Experience: ${jobData.experience}` : ''}

Key Skills & Technologies:
${jobData.skills}

Main Responsibilities:
${jobData.responsibilities}

${jobData.qualifications ? `Required Qualifications:\n${jobData.qualifications}` : ''}

${jobData.benefits ? `Benefits & Perks:\n${jobData.benefits}` : ''}

Please format the job description with clear sections and compelling language that would attract qualified candidates. 
Use a ${tone} tone that reflects the company culture.
${jobData.formatWithMarkdown ? 'Use Markdown formatting with headers (##), bullet points (*), and emphasis where appropriate.' : ''}
${jobData.includeEqualOpportunity ? 'Include a professional Equal Employment Opportunity (EEO) statement at the end of the job description.' : ''}
Make this job description ready to be published immediately.`;
} 